import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { RefreshCw, ShieldCheck, ChevronsRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Slide-the-puzzle human check. Calls onVerified(token) once the server accepts the drag.
export const SliderCaptcha = ({ onVerified }) => {
  const [ch, setCh] = useState(null);
  const [x, setX] = useState(0);
  const [state, setState] = useState("idle"); // idle | dragging | checking | ok | fail
  const drag = useRef(null);
  const track = useRef(null);

  const load = useCallback(async () => {
    setState("idle"); setX(0); onVerified("");
    try { setCh((await axios.get(`${API}/captcha/challenge`)).data); } catch { setState("fail"); }
  }, [onVerified]);
  useEffect(() => { load(); }, [load]);

  const scale = () => (track.current ? track.current.clientWidth / (ch?.width || 300) : 1);
  const maxX = () => (ch ? ch.width - ch.piece_size : 254);

  const start = (clientX) => {
    if (!ch || state === "ok" || state === "checking") return;
    drag.current = { x0: clientX, t0: performance.now(), trace: [0] };
    setState("dragging");
  };
  const move = useCallback((clientX) => {
    if (!drag.current) return;
    const nx = Math.max(0, Math.min(maxX(), (clientX - drag.current.x0) / scale()));
    drag.current.trace.push(Math.round(nx * 10) / 10);
    setX(nx);
  }, [ch]); // eslint-disable-line react-hooks/exhaustive-deps
  const end = useCallback(async () => {
    const d = drag.current;
    if (!d) return;
    drag.current = null;
    setState("checking");
    try {
      const r = await axios.post(`${API}/captcha/verify`, {
        id: ch.id, x: d.trace[d.trace.length - 1], trace: d.trace.slice(-400), duration_ms: performance.now() - d.t0,
      });
      setState("ok"); onVerified(r.data.token);
    } catch {
      setState("fail");
      setTimeout(load, 900);
    }
  }, [ch, load, onVerified]);

  useEffect(() => {
    const mm = (e) => move(e.touches ? e.touches[0].clientX : e.clientX);
    const mu = () => end();
    window.addEventListener("mousemove", mm); window.addEventListener("mouseup", mu);
    window.addEventListener("touchmove", mm, { passive: true }); window.addEventListener("touchend", mu);
    return () => {
      window.removeEventListener("mousemove", mm); window.removeEventListener("mouseup", mu);
      window.removeEventListener("touchmove", mm); window.removeEventListener("touchend", mu);
    };
  }, [move, end]);

  const pct = ch ? (x / ch.width) * 100 : 0;
  const msg = { idle: "Slide the piece into the gap", dragging: "Keep sliding…", checking: "Checking…",
    ok: "Verified — you're human", fail: "Not quite — try again" }[state];

  return (
    <div className={`cap-box cap-${state}`} data-testid="captcha">
      <div className="cap-img" ref={track} style={{ aspectRatio: ch ? `${ch.width} / ${ch.height}` : "300 / 160" }}>
        {ch ? (
          <>
            <img src={ch.bg} alt="Puzzle background" draggable={false} />
            <img src={ch.piece} alt="" draggable={false} className="cap-piece" data-testid="captcha-piece"
              style={{ left: `${pct}%`, top: `${(ch.piece_y / ch.height) * 100}%`, width: `${(ch.piece_size / ch.width) * 100}%` }} />
          </>
        ) : <div className="cap-loading">Loading puzzle…</div>}
        <button type="button" className="cap-refresh" onClick={load} title="New puzzle" data-testid="captcha-refresh"><RefreshCw size={14} /></button>
      </div>
      <div className="cap-track" data-testid="captcha-track">
        <div className="cap-fill" style={{ width: `calc(${pct}% + 22px)` }} />
        <span className="cap-msg" data-testid="captcha-status">{msg}</span>
        <div className="cap-knob" role="slider" aria-label="Slide to complete the puzzle" aria-valuenow={Math.round(pct)}
          style={{ left: `${pct}%` }} data-testid="captcha-handle"
          onMouseDown={(e) => { e.preventDefault(); start(e.clientX); }}
          onTouchStart={(e) => start(e.touches[0].clientX)}>
          {state === "ok" ? <ShieldCheck size={18} /> : <ChevronsRight size={18} />}
        </div>
      </div>
    </div>
  );
};
